/**
 * Presentational board pieces for Seam Crossing Rush: the queue of crossings
 * approaching the governed seam and the witness-component palette. Both are pure
 * — they accept props and return JSX, and they never decide whether a crossing
 * is constructable. A crossing crosses exactly when its attached set equals its
 * required witness tuple; an incomplete tuple is structurally inexpressible.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import type { JSX, KeyboardEvent } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { ArcadeSelectableTile, FlashOverlay } from "../../../games/index.js";
import { componentById } from "./seamCrossingPresentation.js";
import type {
  RushState,
  WitnessComponent,
} from "../algorithms/seamCrossingRush.js";
// The active crossing and the queued crossings share the element shape carried
// by RushState.queue; derive it here rather than depend on an internal name.
type CrossingRequest = RushState["queue"][number];
interface ApproachingSeamProps {
  readonly state: RushState;
  readonly palette: readonly WitnessComponent[];
  readonly accent: string;
  readonly flashToken: number;
  readonly flashColor: string;
  readonly reducedMotion: boolean;
}
export function ApproachingSeam({
  state,
  palette,
  accent,
  flashToken,
  flashColor,
  reducedMotion,
}: ApproachingSeamProps): JSX.Element {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-black/25 p-3"
      style={{ position: "relative" }}
    >
      <div
        role="list"
        aria-label="Crossings approaching the seam"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {state.queue.length === 0 && (
          <p style={{ margin: 0, fontSize: 12.5, color: panelTokens.muted }}>
            No crossings in flight — the next agent is approaching.
          </p>
        )}
        {state.queue.map((request) => (
          <CrossingRequestRow
            key={request.id}
            request={request}
            isActive={request.id === state.activeId}
            palette={palette}
            accent={accent}
          />
        ))}
      </div>
      <FlashOverlay
        token={flashToken}
        color={flashColor}
        reducedMotion={reducedMotion}
      />
    </div>
  );
}
interface CrossingRequestRowProps {
  readonly request: CrossingRequest;
  readonly isActive: boolean;
  readonly palette: readonly WitnessComponent[];
  readonly accent: string;
}
function CrossingRequestRow({
  request,
  isActive,
  palette,
  accent,
}: CrossingRequestRowProps): JSX.Element {
  const patienceLow = request.patienceRemaining <= 4;
  const reqColor = isActive ? accent : panelTokens.border;
  return (
    <PanelSurface.TintedRow
      className="font-mono"
      color={reqColor}
      compact
      heading={request.title}
      muted={!isActive}
      role="listitem"
      trailing={
        <>
          {isActive ? PanelSurface.badge("at front", accent) : null}
          <span
            className="text-[10.5px]"
            style={{
              color: patienceLow ? panelTokens.accentRed : panelTokens.muted,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            patience {String(request.patienceRemaining)}
          </span>
        </>
      }
    >
      <PanelSurface.BadgeGroup className="mt-1.5">
        {request.required.map((id) => {
          const component = componentById(palette, id);
          const held = request.attached.includes(id);
          const color = held ? panelTokens.accentSafe : panelTokens.muted;
          return (
            <span
              key={id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 999,
                fontFamily: panelTokens.mono,
                fontSize: 10.5,
                color,
                border: `1px solid ${color}66`,
                background: held ? `${color}1A` : "transparent",
                textDecoration: held ? "none" : "line-through",
              }}
            >
              <span aria-hidden>{component?.glyph ?? "·"}</span>
              {component?.label ?? id}
              <span aria-hidden>{held ? " ✓" : " ◌"}</span>
            </span>
          );
        })}
      </PanelSurface.BadgeGroup>
    </PanelSurface.TintedRow>
  );
}
interface WitnessPaletteProps {
  readonly palette: readonly WitnessComponent[];
  readonly active: CrossingRequest | null;
  readonly accent: string;
  readonly onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  readonly onToggle: (component: WitnessComponent, held: boolean) => void;
}
export function WitnessPalette({
  palette,
  active,
  accent,
  onKeyDown,
  onToggle,
}: WitnessPaletteProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
      <div
        role="group"
        aria-label="Witness component palette"
        tabIndex={0}
        onKeyDown={onKeyDown}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          outline: "none",
        }}
      >
        {palette.map((component, index) => {
          const held = active?.attached.includes(component.id) ?? false;
          const required = active?.required.includes(component.id) ?? false;
          const color = held
            ? panelTokens.accentSafe
            : required
              ? accent
              : panelTokens.muted;
          return (
            <ArcadeSelectableTile
              key={component.id}
              accent={color}
              ariaLabel={`${held ? "Detach" : "Attach"} ${component.label} (key ${String(index + 1)})`}
              disabled={active === null}
              hotkey={String(index + 1)}
              leading={component.glyph}
              onClick={() => {
                if (active === null) return;
                onToggle(component, held);
              }}
              palette={panelTokens}
              selected={held}
              subtitle={component.summary}
              title={component.label}
            />
          );
        })}
      </div>
    </div>
  );
}
